<template>
  <SatellitesPage>
    <template #actions>
      <router-link :to="{ name: 'SatelliteGroup', params: { id: 'new' } }" class="btn _bg-green sat-btn-inline"><SatIcon name="plus" /> {{ $t("satellites.groups.create") }}</router-link>
    </template>

    <div v-if="loading" class="d-flex justify-content-center py-5">
      <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
    </div>

    <div v-else-if="!groups.length" class="sat-empty">
      <span class="sat-empty__title">{{ $t("satellites.groups.empty") }}</span>
      <span class="sat-empty__text">{{ $t("satellites.groups.description") }}</span>
      <router-link :to="{ name: 'SatelliteGroup', params: { id: 'new' } }" class="btn _bg-green sat-btn-inline"><SatIcon name="plus" /> {{ $t("satellites.groups.create") }}</router-link>
    </div>

    <template v-else>
      <p class="groups-description">{{ $t("satellites.groups.description") }}</p>
      <div class="groups-grid">
        <router-link v-for="group in groups" :key="group.id" :to="{ name: 'SatelliteGroup', params: { id: group.id } }" class="group-card">
          <div class="group-card__head">
            <span class="group-card__name">{{ group.name }}</span>
            <SatIcon name="chevron-right" :size="16" class="group-card__chevron" />
          </div>
          <div class="group-card__stats">
            <span class="group-card__stat" :class="{ _warn: !group.members_count }"><SatIcon name="server" :size="14" /> {{ pl("satellites.counts.satellites", group.members_count) }}</span>
            <span class="group-card__stat"><SatIcon name="globe" :size="14" /> {{ pl("satellites.counts.domains", group.domains_count) }}</span>
          </div>
          <div class="group-card__settings">
            <template v-if="changed(group).length">
              <span v-for="item in changed(group)" :key="item.key" class="sat-chip">{{ item.label }}: {{ item.value }}</span>
            </template>
            <span v-else class="sat-muted">{{ $t("satellites.groups.no_overrides") }}</span>
          </div>
          <div class="group-card__date">{{ $t("satellites.groups.updated", { date: formatDate(group.updated_at) }) }}</div>
        </router-link>
      </div>
    </template>
  </SatellitesPage>
</template>

<script>
import satelliteService from "@/api/satelliteService";
import SatellitesPage from "@/components/Satellites/SatellitesPage.vue";
import SatIcon from "@/components/Satellites/SatIcon.vue";
import satelliteFeatureGuard from "@/components/Satellites/satelliteFeatureGuard";
import { formatMixin, isOk } from "@/components/Satellites/format";
import { changedSettings } from "@/components/Satellites/settingsSchema";

export default {
  name: "SatelliteGroups",
  components: { SatellitesPage, SatIcon },
  mixins: [formatMixin],
  beforeRouteEnter: satelliteFeatureGuard,
  data() {
    return {
      loading: true,
      groups: [],
      definitions: [],
    };
  },
  mounted() {
    Promise.all([satelliteService.getGroups(), satelliteService.getSettingsSchema()])
      .then(([groups, schema]) => {
        if (groups && groups.status === 403) {
          this.$router.replace({ name: "Dashboard" }).catch(() => {});
          return;
        }
        if (isOk(groups)) this.groups = groups.data || [];
        else this.showError(groups);
        if (isOk(schema)) this.definitions = schema.data.definitions || [];
      })
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    // Settings of the group that differ from the defaults
    changed(group) {
      return changedSettings(this, this.definitions, Array.isArray(group.overrides) ? {} : group.overrides);
    },
  },
};
</script>

<style lang="scss" scoped>
.groups-description {
  max-width: 720px;
  margin: 0 0 18px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--sat-muted);
}

.groups-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.group-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px;
  border: 1px solid var(--sat-border);
  border-radius: 16px;
  color: var(--sat-text);
  background: var(--sat-card);
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;

  &:hover {
    border-color: var(--sat-accent);
    box-shadow: var(--sat-shadow);
    color: var(--sat-text);
    text-decoration: none;
    transform: translateY(-1px);

    .group-card__chevron {
      color: var(--sat-accent);
      transform: translateX(2px);
    }
  }
}

.group-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.group-card__name {
  min-width: 0;
  overflow: hidden;
  font-size: 17px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-card__chevron {
  color: var(--sat-muted);
  transition: transform 0.15s, color 0.15s;
}

.group-card__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.group-card__stat {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--sat-text-2);

  &._warn {
    color: var(--sat-warn);
  }
}

.group-card__settings {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 26px;
  font-size: 13px;
}

.group-card__date {
  margin-top: auto;
  font-size: 12px;
  color: var(--sat-muted);
}
</style>
